/**
 * Cart Sage bar (Purrflixx): one-tap "Add" for the curated add-ons in the cart drawer and cart page.
 *
 * Loaded once from layout/theme.liquid (scripts inside the cart drawer never run when Horizon
 * morphs the drawer in after an add), and bound with ONE delegated listener on document, so it
 * keeps working after every section re-render.
 *
 * Each add-on is a real <form action="/cart/add">, so it still works without JS (full-page add -> /cart).
 * With JS: POST /cart/add.js with the cart sections, then dispatch Horizon's CartLinesUpdateEvent
 * (same contract as product-form.js), which morphs the drawer / cart page and updates the cart bubble.
 * If the re-render doesn't land, fall back to a full reload (or /cart).
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const FALLBACK_MS = 3000;

function sectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids);
}

function routes() {
  // @ts-ignore
  return (window.Theme && window.Theme.routes) || {};
}

function setBusy(button, busy) {
  const label = button.querySelector('.pfx-sage__add-label');
  if (busy) {
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    if (label) label.textContent = 'Adding…';
  } else {
    button.removeAttribute('aria-busy');
    button.disabled = false;
    if (label) label.textContent = 'Add';
  }
}

function hardRefresh() {
  // Drawer can't be re-rendered: go to a page that shows the true cart.
  if (/\/cart\/?$/.test(window.location.pathname)) window.location.reload();
  else window.location.assign(routes().cart_url || '/cart');
}

async function addVariant(form, button) {
  const variantId = Number(new FormData(form).get('id'));
  if (!variantId) return hardRefresh();

  setBusy(button, true);

  const deferred = CartLinesUpdateEvent.createPromise();
  // Dispatched from the button (inside cart-items-component, bubbles to document) exactly like
  // product-form.js dispatches from itself; cart-items-component and cart-icon listen on document.
  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      promise: deferred.promise,
    })
  );

  const cartUrl = routes().cart_url || '/cart';
  const addUrl = (routes().cart_add_url || '/cart/add').replace(/\.js$/, '') + '.js';

  try {
    const res = await fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: sectionIds(),
        sections_url: window.location.pathname,
      }),
    });
    const data = await res.json();
    const ajaxCart = await (await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } })).json();

    if (!res.ok || data.status) {
      button.dispatchEvent(
        new CartErrorEvent({ error: data.description || data.message || 'Add to cart failed', code: 'INVALID' })
      );
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: { items: ajaxCart.items, itemCount: ajaxCart.item_count, source: 'pfx-cart-sage', didError: true },
      });
      setBusy(button, false);
      return;
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: {
        items: ajaxCart.items,
        itemCount: ajaxCart.item_count,
        sections: data.sections,
        source: 'pfx-cart-sage',
        didError: false,
      },
    });

    // Success re-renders the section and this row disappears (the item is now in the cart).
    // If the button is still on screen and busy after a moment, the re-render didn't happen.
    setTimeout(() => {
      if (button.isConnected && button.getAttribute('aria-busy') === 'true') hardRefresh();
    }, FALLBACK_MS);
  } catch (error) {
    console.error('[pfx-cart-sage] add failed, falling back to a normal form post', error);
    deferred.reject(error);
    // Native submit = no-JS path: POST /cart/add then Shopify redirects to /cart.
    HTMLFormElement.prototype.submit.call(form);
  }
}

// @ts-ignore
if (!window.__pfxCartSageBound) {
  // @ts-ignore
  window.__pfxCartSageBound = true;

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('[data-pfx-sage-form]')) return;
      event.preventDefault();
      const button = form.querySelector('[data-pfx-sage-add]');
      if (!(button instanceof HTMLButtonElement) || button.getAttribute('aria-busy') === 'true') return;
      addVariant(form, button);
    },
    true
  );
}
